import React, { useState, useEffect } from 'react';
import { getParticipants, addParticipant, removeParticipant } from '../../services/participantsService';
import { sendConversationMessage } from '../../services/notifications.service';

const Participants = ({ conversationId, participants, setParticipants }) => {
  const [loading, setLoading] = useState(false); // You can set this externally too if needed
  const [error, setError] = useState(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [label, setLabel] = useState('');
  const [formError, setFormError] = useState('');
  const [isOpen, setIsOpen] = useState(true);

  const isE164Valid = (number) => /^\+?[1-9]\d{1,14}$/.test(number);

  const handleAddParticipant = async () => {
    setFormError('');
    if (!isE164Valid(phoneNumber)) {
      setFormError('Please enter a valid phone number in E.164 format (e.g., +15555555555).');
      return;
    }
    if (!label.trim()) {
      setFormError('Please provide a label for the participant.');
      return;
    }

    const newParticipant = {
      conversationId,
      phoneNumber,
      name: label.trim(),
      role: 'member',
      isRequired: false,
    };

    try {
      const added = await addParticipant(newParticipant);
      setParticipants(prev => [...prev, added]);
      setPhoneNumber('');
      setLabel('');
      const newMessage = `${newParticipant.name} has been added to the conversation.`;
      try {
        // Call your sendVideoSMS function
        const author = 'DigitalPathways'; // Author
        await sendConversationMessage(
          conversationId,
          newMessage, // Message content
          author // Author
        );
      } catch (smsError) {
        console.error('Error sending SMS via API:', smsError);
        // Continue with update even if SMS fails - at least we'll have the message in our DB
      }      
    } catch (err) {
      setFormError('Unable to add participant.');
    }
  };

  const handleRemoveParticipant = async (participant) => {
    try {
      const newMessage = `${participant.name} has been removed from the conversation.`;
  
      try {
        const author = 'DigitalPathways'; // Author
        await sendConversationMessage(
          conversationId,
          newMessage,
          author
        );
      } catch (smsError) {
        console.error('Error sending SMS via API:', smsError);
        // Proceed even if message delivery fails
      }
  
      await removeParticipant(participant.id);
      setParticipants(prev => prev.filter(p => p.id !== participant.id));
    } catch (err) {
      setError('Unable to remove participant.');
    }
  };
  

  const isSubmitter = (role) => role?.toLowerCase() === 'submitter';

  if (loading) return <p className="text-gray-600">Loading participants...</p>;
  if (error) return <p className="text-red-600">{error}</p>;

   return (
    <div className="p-6 font-sans">
      {/* Accordion Header */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between cursor-pointer select-none"
      >
        <h3 className="text-xl font-semibold pb-2 mb-4">
          Participants
        </h3>
        <span className="text-blue-600 text-sm">
          {isOpen ? 'Hide ▲' : 'Show ▼'}
        </span>
      </div>

      {/* Accordion Body */}
      {isOpen && (
        <>
          <ul className="space-y-3 mb-6">
            {participants.map(p => (
              <li key={p.id} className="flex items-center justify-between border rounded p-3">
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {p.name || '(No label)'}{' '}
                    {isSubmitter(p.role) && <em className="text-sm text-gray-500">(Submitter)</em>}
                  </span>
                  <span className="text-sm text-gray-600">{p.phoneNumber}</span>
                </div>
                <button
                  onClick={() => handleRemoveParticipant(p)}
                  disabled={isSubmitter(p.role)}
                  className={`px-3 py-1 rounded text-white text-sm ${
                    isSubmitter(p.role)
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>

          <div className="bg-gray-50 p-4 rounded shadow-sm">
            <h4 className="text-lg font-medium mb-3">Add Participant</h4>
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
              <input
                type="text"
                placeholder="Phone (e.g. 5555555555)"
                value={phoneNumber}
                onChange={(e) => {
                  let val = e.target.value.trim();
                  if (val.startsWith('+1')) {
                    setPhoneNumber(val);
                  } else {
                    const digitsOnly = val.replace(/\D/g, '');
                    setPhoneNumber(`+1${digitsOnly}`);
                  }
                }}
                className="border px-3 py-2 rounded w-full md:w-64"
              />
              <input
                type="text"
                placeholder="Label (e.g. Parent)"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="border px-3 py-2 rounded w-full md:w-48"
              />
              <button
                onClick={handleAddParticipant}
                disabled={!phoneNumber || !label}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
            {formError && <p className="text-red-600 mt-2">{formError}</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default Participants;

